import type React from "react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import type { client } from "@duress/shared";
import { fmtHours, fmtInt, fmtPct } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useClientSnapshot } from "./useSnapshot";
import { Select } from "@/components/ui/select";
import { CompareRow } from "@/components/CompareRow";

interface KpiRow {
  key: keyof client.ClientSnapshotKpis;
  label: string;
  format: (v: number) => string;
  direction: "higher" | "lower" | "neutral";
}

const KPI_ROWS: KpiRow[] = [
  { key: "total_sessions", label: "Total Sessions", format: fmtInt, direction: "higher" },
  { key: "productive_hours", label: "Productive Hours", format: fmtHours, direction: "higher" },
  { key: "total_hours", label: "Total Hours", format: fmtHours, direction: "higher" },
  { key: "extended_session_rate", label: "Extended Session Rate", format: fmtPct, direction: "lower" },
  { key: "fleet_idle_rate_pct", label: "Fleet Idle Rate", format: fmtPct, direction: "lower" },
  { key: "network_adoption_pct", label: "Network Adoption", format: fmtPct, direction: "higher" },
  { key: "active_teams", label: "Active Teams", format: fmtInt, direction: "higher" },
  { key: "micro_sessions_excluded", label: "Micro-Sessions Excluded", format: fmtInt, direction: "neutral" },
];

function pickKpi(kpis: client.ClientSnapshotKpis, key: keyof client.ClientSnapshotKpis): number {
  switch (key) {
    case "total_sessions":
      return kpis.total_sessions;
    case "productive_hours":
      return kpis.productive_hours;
    case "total_hours":
      return kpis.total_hours;
    case "extended_session_rate":
      return kpis.extended_session_rate;
    case "fleet_idle_rate_pct":
      return kpis.fleet_idle_rate_pct;
    case "network_adoption_pct":
      return kpis.network_adoption_pct;
    case "active_teams":
      return kpis.active_teams;
    case "total_teams":
      return kpis.total_teams;
    case "micro_sessions_excluded":
      return kpis.micro_sessions_excluded;
  }
}

function formatMonthLabel(value: string): string {
  const [year, month] = value.split("-");
  const m = parseInt(month ?? "0", 10);
  const months = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[m] ?? ""} ${year ?? ""}`;
}

export function ComparisonPage(): React.JSX.Element {
  const { slugA = "", slugB = "" } = useParams<{ slugA: string; slugB: string }>();
  const [monthA, setMonthA] = useState<string | null>(null);
  const [monthB, setMonthB] = useState<string | null>(null);

  const { data: snapA, isLoading: la, error: ea } = useClientSnapshot(slugA, monthA);
  const { data: snapB, isLoading: lb, error: eb } = useClientSnapshot(slugB, monthB);

  if (la || lb) {
    return <p className="text-sm text-muted-foreground">Loading comparison…</p>;
  }
  if (ea || eb || !snapA || !snapB) {
    return (
      <div className="space-y-4">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to clients
        </Link>
        <p className="text-sm text-destructive">
          One or both clients have no period data for the selected filters.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to clients
      </Link>

      <div>
        <h1 className="text-3xl font-head font-semibold tracking-tight">Comparison</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Side-by-side averages across the selected periods.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <ClientHeaderCard snapshot={snapA} month={monthA} onMonthChange={setMonthA} accent="phoenix" />
        <ClientHeaderCard snapshot={snapB} month={monthB} onMonthChange={setMonthB} accent="eagle" />
      </div>

      <div className="card-duress overflow-hidden">
        {KPI_ROWS.map((row) => (
          <CompareRow
            key={row.key}
            spec={row}
            a={{ name: snapA.clientName, value: pickKpi(snapA.kpis, row.key) }}
            b={{ name: snapB.clientName, value: pickKpi(snapB.kpis, row.key) }}
            extra={extraForRow(row, snapA.kpis, snapB.kpis)}
          />
        ))}
      </div>
    </div>
  );
}

function extraForRow(
  row: KpiRow,
  a: client.ClientSnapshotKpis,
  b: client.ClientSnapshotKpis,
): string | null {
  if (row.key === "active_teams") {
    return `vs total teams: ${Math.round(a.total_teams)} / ${Math.round(b.total_teams)}`;
  }
  return null;
}

function ClientHeaderCard({
  snapshot,
  month,
  onMonthChange,
  accent,
}: {
  snapshot: client.ClientSnapshot;
  month: string | null;
  onMonthChange: (m: string | null) => void;
  accent: "phoenix" | "eagle";
}): React.JSX.Element {
  const accentClass =
    accent === "phoenix" ? "bg-phoenix-orange" : "bg-eagle-blue";
  return (
    <section className="card-duress overflow-hidden">
      <div className={cn("h-1.5", accentClass)} />
      <div className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to={`/clients/${snapshot.slug}`}
              className="font-head text-lg font-semibold hover:text-phoenix-orange"
            >
              {snapshot.clientName}
            </Link>
            {snapshot.industries.length > 0 ? (
              <p className="text-xs text-muted-foreground mt-0.5">
                {snapshot.industries.join(", ")}
              </p>
            ) : null}
          </div>
          <span className="section-label">
            {snapshot.periodCount}{" "}
            {snapshot.periodCount === 1 ? "period" : "periods"}
          </span>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <span className="section-label">Period</span>
          <Select
            value={month ?? "all"}
            onChange={(e) => onMonthChange(e.currentTarget.value === "all" ? null : e.currentTarget.value)}
          >
            <option value="all">All time</option>
            {snapshot.availableMonths.map((m) => (
              <option key={m} value={m}>
                {formatMonthLabel(m)}
              </option>
            ))}
          </Select>
        </label>
      </div>
    </section>
  );
}
